import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import { noteError } from './lib/diagnostics';
import { startArWarm } from './ar';
import { resolveMode } from './config';

/**
 * Global error taps. On the kiosk nobody is watching a console, so anything
 * thrown needs to reach the admin view.
 */
window.addEventListener('error', (e) => noteError('window.error', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) => noteError('unhandledrejection', e.reason));

/**
 * Start warming the AR engine before React renders anything.
 *
 * Kiosk only, and deliberately outside the component tree: this used to live in
 * KioskFlow's mount effect, which tied it to a component lifecycle and lost it
 * on any remount. Camera Kit's bootstrap is the longest pole in the flow and the
 * unit typically boots before the venue network is up, so it needs to start as
 * early as possible and keep retrying on its own. See src/ar/cameraKitWarm.ts.
 *
 * The web flow warms on its own title screen instead — a phone arrives with a
 * network and a guest who just tapped a link, so there is nothing to recover
 * from and no reason to spend a visitor's data before they have opted in.
 */
if (resolveMode() === 'kiosk') startArWarm();

const root = document.getElementById('root');
if (!root) throw new Error('#root missing from index.html');

createRoot(root).render(
  // StrictMode double-invokes effects in dev, which is exactly the pressure we
  // want on the AR init/destroy pair and on the object URL registry — a leak or
  // a double-init shows up here rather than at hour three on the kiosk.
  <StrictMode>
    <App />
  </StrictMode>,
);
